import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Row, Col, Form, FormGroup, Label } from 'reactstrap';
import styles from './CustomerInfo.module.css';
import { DropDownAutoComplete } from '../inputs/DropDownAutoComplete';
import SimpleReactValidator from 'simple-react-validator';
import { useHistory, useLocation } from 'react-router';
import Cleave from 'cleave.js/react';
import { BrandingContext } from '../contexts/ContractContext';
import { SalesContext } from '../contexts/SalesContext';
import SaveModal from '../modals/SaveModal';
import classNames from 'classnames';
import InfoTooltip from '../shared/InfoTooltip/InfoTooltip';
import OrderSummary from './OrderSummary';
import RecommendedAddressModal from './RecommendedAddressModal';
import IncorrectAddressModal from './IncorrectAddressModal';
import StandardInput from '../shared/StandardInput/StandardInput';
import CallBanner from '../shared/CallBanner/CallBanner';
import useLoadAtTop from '../_ReactHooks/useLoadAtTop';
import { dataLayerPush, getMelissaBypassValidation } from '../../helpers';
import { verifyAddress, VerificationResponseCodes } from '../api/addressverification';
import { usePageTitle } from '../_ReactHooks';
import CheckboxInput from '../shared/CheckboxInput/CheckboxInput';
import getClientContent from '../_BrandingProvider/brandContent';
import SpinnerButton from '../shared/SpinnerButton/SpinnerButton';
import { useCookies } from 'react-cookie';
import { CancelSaleModal } from './CancelSaleModal';

let btn = 'btn';
let btnLink = 'btnLink';
let btnsecondary = 'btn-secondary';

const CustomerInfo = (props) => {
    /**
     *  ==================================
     *  ============= HOOKS ==============
     *  ===================================
     */
    useLoadAtTop();
    usePageTitle('Customer Information');
    const location = useLocation();
    const history = useHistory();

    /**
     *  ==================================
     *  ============ CONTEXTS ============
     *  ==================================
     */
    const branding = useContext(BrandingContext);
    const clientId = branding.clientId || 'AIG';
    const clientLower = clientId?.toLowerCase();

    const context = useContext(SalesContext);
    const { planData, isAgent, taxRate, deviceData, contract } = context.data;

    /**
     *  ==================================
     *  ============== STATE =============
     *  ==================================
     */
    const [formData, setFormData] = useState({
        invitationNumber: location.state?.planDetails?.invitationNumber ?? '0',
        firstName: location.state?.planDetails?.firstName ?? '',
        lastName: location.state?.planDetails?.lastName ?? '',
        phone: location.state?.planDetails?.phone ?? '',
        email: location.state?.planDetails?.email ?? '',
        serviceAddress: {
            address1: location.state?.planDetails?.serviceAddress?.address1 ?? '',
            address2: location.state?.planDetails?.serviceAddress?.address2 ?? '',
            city: location.state?.planDetails?.serviceAddress?.city ?? '',
            state: location.state?.planDetails?.serviceAddress?.state ?? 'NJ',
            zip: location.state?.planDetails?.serviceAddress?.zip ?? '',
            stateId: location.state?.planDetails?.serviceAddress?.stateId ?? 39
        },
        mailingAddress: {
            address1: location.state?.planDetails?.mailingAddress?.address1 ?? '',
            address2: location.state?.planDetails?.mailingAddress?.address2 ?? '',
            city: location.state?.planDetails?.mailingAddress?.city ?? '',
            state: location.state?.planDetails?.mailingAddress?.state ?? '',
            zip: location.state?.planDetails?.mailingAddress?.zip ?? '',
            stateId: location.state?.planDetails?.mailingAddress?.stateId ?? 0
        },
        billingAddress: {
            address1: location.state?.planDetails?.billingAddress?.address1 ?? '',
            address2: location.state?.planDetails?.billingAddress?.address2 ?? '',
            city: location.state?.planDetails?.billingAddress?.city ?? '',
            state: location.state?.planDetails?.billingAddress?.state ?? '',
            zip: location.state?.planDetails?.billingAddress?.zip ?? '',
            stateId: location.state?.planDetails?.billingAddress?.stateId ?? 0
        },
        doNotSolicit: location.state?.planDetails?.doNotSolicit ?? null,
        doNotSolicitDate: new Date('01/01/01'),
        termsAndCondition: false,
        riskProfileModel: location.state?.riskProfile,
        activeAddPlans: location.state?.activeAddPlans,
        isMailingAddress: true,
        serviceAsMailingAddress: true,
        plan: location.state?.plan,
        planDocuments: ''
    });
    const [cookies] = useCookies();
    const [states, setStates] = useState([]);
    const [termsAndCondition, setTermsAndCondition] = useState(false);
    const [, forceUpdate] = useState();
    const [isOpenAddressValidationModal, setIsOpenAddressValidationModal] = useState(false);
    const [isOpenUnableAddressModal, setIsOpenUnableAddressModal] = useState(false);
    const [recommendedAddress, setRecommendedAddress] = useState({});
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [validationCheck, setValidationCheck] = useState(false);
    const [showServiceAddress, setShowServiceAddress] = useState(false);
    const [showMailingAddress, setShowMailingAddress] = useState(false);
    const [totalTaxPrice, setTotalTaxPrice] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [bypassServiceMelissaValidation, setBypassServiceMelissaValidation] = useState(false);
    const [bypassMailingMelissaValidation, setBypassMailingMelissaValidation] = useState(false);
    const [showCancelSaleModal, setShowCancelSaleModal] = useState(false);
    const contractNumber = location.state?.contract?.contractNumber || contract?.contractNumber || '';

    /**
     *  ==================================
     *  ============= EFFECT =============
     *  ==================================
     */
    // ===== GOOGLE TAG MANAGER FOR ENHANCED ECOMMERCE ANALYTICS =====
    useEffect(() => {
        dataLayerPush('Pageview', {
            pagePath: '/customerinfo',
            pageTitle: 'NJR Retail Service Plans - Customer Information'
        });

        dataLayerPush(null, { ecommerce: null }); // CLEAR EXISTING DATA FROM PREVIOUS ECOMMERCE EVENT
        const {
            plan,
            planTotal,
            activePlan,
            activeAddPlans,
            additionalTotal,
            selectedAddPlans,
            promocodeObj,
            tsf,
            selectedPlan: { monthly, yearly, title, includes }
        } = planData;

        if (totalTaxPrice) {
            const additionalPlansForGtm = [];
            selectedAddPlans.map((aP, i) =>
                additionalPlansForGtm.push({
                    item_id: activeAddPlans[i],
                    item_name: aP.title,
                    item_category: 'add_on_plan',
                    price:
                        tsf?.name == 'lower'
                            ? aP?.lowerTsfPrice
                            : tsf?.name == 'higher'
                              ? aP?.higherTsfPrice
                              : plan === 'monthly'
                                ? aP?.monthly
                                : aP?.yearly,
                    paymentPlan: plan
                })
            );
            dataLayerPush('begin_checkout', {
                ecommerce: {
                    currency: 'USD',
                    value: planTotal + additionalTotal + totalTaxPrice,
                    //tax getting only tax for primary plan
                    tax: totalTaxPrice,
                    coupon: promocodeObj?.name,
                    items: [
                        {
                            item_id: activePlan,
                            item_name: title,
                            item_category: 'primary_plan',
                            includes,
                            price: plan === 'monthly' ? monthly : yearly,
                            paymentPlan: plan
                        },
                        ...additionalPlansForGtm
                    ]
                }
            });
        }
    }, [totalTaxPrice]);
    // ========= END GOOGLE TAG MANAGER ==================
    useEffect(() => {
        // API CALLS
        getStatesFromAPI();
        if (context.data.formData) {
            setFormData(context.data.formData);
        }

        getMelissaBypassValidation(2, clientId).then((data) => {
            setBypassServiceMelissaValidation(data.clientId);
        });

        getMelissaBypassValidation(3, clientId).then((data) => {
            setBypassMailingMelissaValidation(data.clientId);
        });

        const totalTax = parseFloat(
            (
                (context.data.planData.planTotal + parseFloat(context.data.planData.additionalTotal))
                * parseFloat(taxRate)
            ).toFixed(2)
        );

        setTotalTaxPrice(totalTax);
    }, []);

    /**
     *  ==================================
     *  CONTINUE COMPONENT VARS AND FUNCTIONS
     *  ==================================
     */
    const handleBackbutton = () => {
        context.updateFormData(formData);

        if (clientLower === 'njr') {
            history.push('/shopplan');
        } else {
            history.push('/buy/reviewplan');
        }
    };

    const simpleValidator = useRef(
        new SimpleReactValidator({
            messages: {
                phone: 'Please enter a valid phone number.',
                email: 'Please enter a valid email.',
                required: ':attribute is required.'
            },
            element: (message) => (
                <div
                    className='errorMsg'
                    aria-live='polite'
                >
                    {message.charAt(0).toUpperCase() + message.slice(1)}
                </div>
            ),
            validators: {
                valid_phone: {
                    message: 'Please enter a valid phone number.',
                    rule: (val, params, validator) => {
                        return !new RegExp('^([0-9])\\1{9}$').test(val.match(/\d+/g).join(''));
                    }
                }
            }
        })
    );

    const handleInputChange = (e) => {
        let { name, value } = e.target;
        value = name === 'phone' ? e.target.rawValue : value;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleServiceAddressChange = (e) => {
        let { name, value } = e.target;
        let { serviceAddress } = formData;
        setFormData({
            ...formData,
            serviceAddress: {
                ...serviceAddress,
                [name]: value
            }
        });
    };

    const handleMailingAddressChange = (e) => {
        let { name, value } = e.target;
        let { mailingAddress } = formData;
        setFormData({
            ...formData,
            mailingAddress: {
                ...mailingAddress,
                [name]: value
            }
        });
    };

    const handleStateDropDown = (e) => {
        var state = states.find(({ data }) => data === e).abbreviation;
        let { mailingAddress } = formData;
        setFormData({
            ...formData,
            mailingAddress: {
                ...mailingAddress,
                stateId: e,
                state
            }
        });
    };

    const getStatesFromAPI = useCallback(() => {
        fetch(`api/party/states/${clientId}`)
            .then((res) => res.json())
            .then((data) => {
                const statesFromAPI = data.map((s) => {
                    return {
                        data: s.stateId,
                        display: s.name,
                        abbreviation: s.abbreviation
                    };
                });
                setStates(statesFromAPI);
            });
    });

    const handleContinue = async (e) => {
        e.preventDefault();
        let zipValue = dynamicContent?.showServiceAddress
            ? formData?.serviceAddress?.zip
            : formData?.mailingAddress?.zip;

        if (simpleValidator.current.allValid() && zipValue?.length == 5 && !isNaN(zipValue)) {
            setIsLoading(true);
            setValidationCheck(false);

            if (dynamicContent?.showServiceAddress) {
                let serviceAddress = {
                    address1: formData.serviceAddress.address1,
                    address2: formData.serviceAddress.address2,
                    city: formData.serviceAddress.city,
                    zip: formData.serviceAddress.zip,
                    state: formData.serviceAddress.state,
                    stateId: formData.serviceAddress.stateId
                };

                if (!bypassServiceMelissaValidation) {
                    await verifyAddress(serviceAddress, clientId, onServiceAddressVerified);
                } else {
                    await saveVerifiedAddress(serviceAddress, true);
                }
            } else {
                const mailingAddress = {
                    address1: formData.mailingAddress.address1,
                    address2: formData.mailingAddress.address2,
                    city: formData.mailingAddress.city,
                    zip: formData.mailingAddress.zip,
                    state: formData.mailingAddress.state,
                    stateId: formData.mailingAddress.stateId
                };

                if (!bypassMailingMelissaValidation) {
                    verifyAddress(mailingAddress, clientId, onMailingAddressVerified);
                } else {
                    saveVerifiedAddress(mailingAddress, false);
                }
            }
        } else {
            setValidationCheck(true);
            simpleValidator.current.showMessages();
            forceUpdate(1);
        }
    };

    const onServiceAddressVerified = (result) => {
        var stateInfo = '';
        if (
            result.code !== VerificationResponseCodes.error
            && result.code !== VerificationResponseCodes.invalidAddress
            && result.code !== VerificationResponseCodes.thirdpartyerror
        ) {
            stateInfo = states.filter((el) => {
                return el.abbreviation === result.returnedAddress.stateAbbreviation;
            });
        }
        if (result.code === VerificationResponseCodes.addressVerified) {
            setShowServiceAddress(false);
            setShowMailingAddress(false);
            setIsOpenAddressValidationModal(false);
            var addressToSave = {
                address1: result.returnedAddress.address1,
                address2: result.returnedAddress.address2 || result.returnedAddress?.suite,
                city: result.returnedAddress.city,
                zip: result.returnedAddress.zip,
                state: result.returnedAddress.stateAbbreviation,
                stateId: stateInfo.length > 0 ? stateInfo[0].data : formData.serviceAddress.stateId
            };
            saveVerifiedAddress(addressToSave, true);
        } else if (result.code === VerificationResponseCodes.addressChanged) {
            setRecommendedAddress({
                serviceAddress: {
                    address1: result.returnedAddress.address1,
                    address2: result.returnedAddress.address2 || result.returnedAddress?.suite,
                    city: result.returnedAddress.city,
                    zip: result.returnedAddress.zip,
                    state: result.returnedAddress.stateAbbreviation,
                    stateId: stateInfo.length > 0 ? stateInfo[0].data : formData.serviceAddress.stateId
                }
            });
            setShowServiceAddress(true);
            setShowMailingAddress(false);
            setIsOpenAddressValidationModal(true);
        } else {
            setShowServiceAddress(true);
            setShowMailingAddress(false);
            setIsOpenAddressValidationModal(false);
            setIsOpenUnableAddressModal(true);
        }
    };

    const onMailingAddressVerified = (result) => {
        var stateInfo = '';
        if (
            result.code !== VerificationResponseCodes.error
            && result.code !== VerificationResponseCodes.invalidAddress
            && result.code !== VerificationResponseCodes.thirdpartyerror
        ) {
            stateInfo = states.filter((el) => {
                return el.abbreviation === result.returnedAddress.stateAbbreviation;
            });
        }
        if (result.code === VerificationResponseCodes.addressVerified) {
            setShowServiceAddress(false);
            setShowMailingAddress(false);
            setIsOpenAddressValidationModal(false);
            const addressToSave = {
                address1: result.returnedAddress.address1,
                address2: result.returnedAddress.address2 || result.returnedAddress?.suite,
                city: result.returnedAddress.city,
                zip: result.returnedAddress.zip,
                state: result.returnedAddress.stateAbbreviation,
                stateId: stateInfo.length > 0 ? stateInfo[0].data : formData.mailingAddress.stateId
            };
            saveVerifiedAddress(addressToSave, false);
        } else if (result.code === VerificationResponseCodes.addressChanged) {
            setRecommendedAddress({
                mailingAddress: {
                    address1: result.returnedAddress.address1,
                    address2: result.returnedAddress.address2 || result.returnedAddress?.suite,
                    city: result.returnedAddress.city,
                    zip: result.returnedAddress.zip,
                    state: result.returnedAddress.stateAbbreviation,
                    stateId: stateInfo.length > 0 ? stateInfo[0].data : formData.mailingAddress.stateId
                }
            });
            setShowServiceAddress(false);
            setShowMailingAddress(true);
            setIsOpenAddressValidationModal(true);
        } else {
            setShowServiceAddress(false);
            setShowMailingAddress(true);
            setIsOpenAddressValidationModal(false);
            setIsOpenUnableAddressModal(true);
        }
    };

    const saveVerifiedAddress = async (addressToSave, isServiceAddress) => {
        hideModal();
        if (isServiceAddress) {
            if (formData.serviceAsMailingAddress) {
                formData.mailingAddress.address1 = formData.serviceAddress.address1;
                formData.mailingAddress.address2 = formData.serviceAddress.address2;
                formData.mailingAddress.city = formData.serviceAddress.city;
                formData.mailingAddress.state = formData.serviceAddress.state;
                formData.mailingAddress.zip = formData.serviceAddress.zip;
                formData.mailingAddress.stateId = formData.serviceAddress.stateId;
            }
            //Service Address
            formData.serviceAddress.address1 = addressToSave.address1;
            formData.serviceAddress.address2 = addressToSave.address2;
            formData.serviceAddress.city = addressToSave.city;
            formData.serviceAddress.state = addressToSave.state;
            formData.serviceAddress.zip = addressToSave.zip;
            formData.serviceAddress.stateId = addressToSave.stateId;

            let mailingAddress = {
                address1: formData.mailingAddress.address1,
                address2: formData.mailingAddress.address2,
                city: formData.mailingAddress.city,
                zip: formData.mailingAddress.zip,
                state: formData.mailingAddress.state,
                stateId: formData.mailingAddress.stateId
            };

            //Billing Address
            if (formData.billingAddress) {
                formData.billingAddress.address1 = formData.mailingAddress.address1;
                formData.billingAddress.address2 = formData.mailingAddress.address2;
                formData.billingAddress.city = formData.mailingAddress.city;
                formData.billingAddress.state = formData.mailingAddress.state;
                formData.billingAddress.zip = formData.mailingAddress.zip;
                formData.billingAddress.stateId = formData.mailingAddress.stateId;
            }

            if (!bypassMailingMelissaValidation) {
                verifyAddress(mailingAddress, clientId, onMailingAddressVerified);
            } else {
                saveVerifiedAddress(mailingAddress, false);
            }
        } else {
            //Mailing Address
            formData.mailingAddress.address1 = addressToSave.address1;
            formData.mailingAddress.address2 = addressToSave.address2;
            formData.mailingAddress.city = addressToSave.city;
            formData.mailingAddress.state = addressToSave.state;
            formData.mailingAddress.zip = addressToSave.zip;
            formData.mailingAddress.stateId = addressToSave.stateId;

            //Billing Address
            formData.billingAddress.address1 = formData.mailingAddress.address1;
            formData.billingAddress.address2 = formData.mailingAddress.address2;
            formData.billingAddress.city = formData.mailingAddress.city;
            formData.billingAddress.state = formData.mailingAddress.state;
            formData.billingAddress.zip = formData.mailingAddress.zip;
            formData.billingAddress.stateId = formData.mailingAddress.stateId;

            context.updateFormData(formData);

            if (clientLower === 'njr') {
                createPlanContract();
            } else {
                getTaxAmount(addressToSave);
            }
        }
    };

    const createPlanContract = async () => {
        let riskProfile = planData.selectedPlan?.riskProfile;
        let addOns = planData.selectedAddPlans.map((i) => i.riskProfile);
        let statusCode = 0;
        let isAgent = sessionStorage.getItem('isAgentEntry') ?? false;

        await fetch(`api/Contract/CreatePlanContract/${clientId}/${isAgent}`, {
            method: 'post',
            headers: {
                'Content-Type': 'application/json',
                XSRFTOKEN: cookies.XSRFTOKEN
            },
            body: JSON.stringify({
                invitationNumber: formData.invitationNumber ? formData.invitationNumber : '0',
                firstName: formData.firstName ? formData.firstName : '',
                lastName: formData.lastName ? formData.lastName : '',
                phone: formData.phone ? formData.phone : '',
                email: formData.email ? formData.email : '',
                serviceAddress: formData.serviceAddress,
                mailingAddress: formData.mailingAddress,
                billingAddress: formData.billingAddress,
                doNotSolicit: context.data.formData.doNotSolicit
                    ? context.data.formData.doNotSolicit
                    : new Date('01/01/01'),
                termsAndCondition: false,
                riskProfileModel: riskProfile,
                activeAddPlans: addOns,
                isPayment: false,
                savedStep: '',
                Paid: false,
                autoRenewal: true,
                promoCode: context.data.isPromoApplied ? context.data.promocodeObj.name : null,
                tsfAmount: parseFloat(planData?.tsf?.term)?.toFixed(2)
            })
        })
            .then(function (res) {
                statusCode = res.status;
                return res.json();
            })
            .then((contractData) => {
                if (statusCode === 200) {
                    formData.invitationNumber = contractData.clientContractNumber;
                    let validData = { ...formData };
                    history.push({
                        pathname: '/ReviewPlanDetails',
                        state: {
                            data: validData
                        }
                    });
                }
            });
    };

    const getTaxAmount = (data) => {
        fetch(`api/tax/calculate/${clientLower}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                XSRFTOKEN: cookies.XSRFTOKEN
            },
            body: JSON.stringify({
                calculateTaxFlag: true,
                confirmAddressFlag: false,
                currencyCode: 'USD',
                customer: {
                    address1: null,
                    city: null,
                    country: 'US',
                    state: null,
                    postalCode: data?.zip || formData.mailingAddress?.zip || ''
                },
                totalAmount: deviceData?.planPrice,
                contractNumber: contractNumber
            })
        }).then((res) => {
            if (res.ok) {
                res.json().then((data) => {
                    context.updateDeviceData({
                        ...deviceData,
                        zip: data?.zip || formData.mailingAddress?.zip || '',
                        taxAmount: data?.calculatedTaxTotalAmount ?? 0
                    });

                    const totalPrice = (
                        parseFloat(deviceData?.planPrice ?? 0)
                        + parseFloat(data?.calculatedTaxTotalAmount ?? deviceData?.taxAmount ?? 0)
                    )?.toFixed(2);
                    const planDetails = {
                        invitationNumber: formData.invitationNumber ?? '0',
                        amount: totalPrice,
                        clientId: clientId,
                        contractNumber: contractNumber,
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        phone: formData.phone,
                        email: formData.email,
                        doNotSolicit: formData.doNotSolicit,
                        riskProfileModel: '',
                        yearlyRiskProfileModel: '',
                        activeAddPlans: [],
                        activeAddPlansYearly: [],
                        planData: {
                            selectedPlan: {
                                riskProfile: ''
                            },
                            selectedAddPlans: [],
                            activePlan: null,
                            activeAddPlans: [],
                            activeAddPlansYearly: [],
                            plan: '',
                            annualPlanTotal: null,
                            tsf: null
                        },
                        verifiedAddress: {
                            address1: formData.mailingAddress.address1,
                            address2: formData.mailingAddress.address2,
                            city: formData.mailingAddress.city,
                            state: formData.mailingAddress.state,
                            stateId: formData.mailingAddress.stateId,
                            zip: formData.mailingAddress.zip
                        },
                        billingAddress: {
                            address1: formData.billingAddress.address1,
                            address2: formData.billingAddress.address2,
                            city: formData.billingAddress.city,
                            state: formData.billingAddress.state,
                            stateId: formData.billingAddress.stateId,
                            zip: formData.billingAddress.zip
                        },
                        serviceAddress: {
                            address1: '',
                            address2: '',
                            city: '',
                            state: '',
                            zip: '',
                            stateId: ''
                        },
                        mailingAddress: {
                            address1: formData.mailingAddress?.address1,
                            address2: formData.mailingAddress?.address2,
                            city: formData.mailingAddress?.city,
                            state: formData.mailingAddress?.state,
                            stateId: formData.mailingAddress?.stateId,
                            zip: formData.mailingAddress?.zip
                        },
                        promoCode: '',
                        taxAmount: data?.calculatedTaxTotalAmount || deviceData?.taxAmount,
                        planDocumentsType: '',
                        isAgent: isAgent,
                        taxRate: null,
                        tsf: null,
                        contractData: contract,
                        deviceInfo: deviceData
                    };

                    history.push({
                        pathname: '/sales/payment',
                        state: {
                            planDetails: planDetails,
                            cookies: cookies
                        }
                    });
                });
            } else {
                setIsLoading(false);
                console.error(res.status);
            }
        });
    };

    const handleCancelSale = () => {
        history.push('/');
    };

    const hideModal = () => {
        setIsOpenUnableAddressModal(false);
        setIsOpenAddressValidationModal(false);
    };

    const handleDoNotSolicit = () => {
        let solicitDate;
        setTermsAndCondition(!termsAndCondition);
        solicitDate = !termsAndCondition ? new Date() : null;
        setFormData({
            ...formData,
            doNotSolicit: solicitDate,
            termsAndCondition: !termsAndCondition
        });
    };

    const handleServiceAsMailing = () => {
        setFormData({
            ...formData,
            serviceAsMailingAddress: !formData.serviceAsMailingAddress,
            mailingAddress: !formData.serviceAddress == true ? formData.serviceAddress : formData.mailingAddress,
            billingAddress: formData.mailingAddress
        });
    };

    const handleSavedData = (isSaved, invitationNumber) => {
        if (isSaved) {
            history.push({
                pathname: '/confirmation',
                state: { invitationNumber: invitationNumber }
            });
        }
    };

    const handleShowActionModal = (event) => {
        event.preventDefault();

        setFormData({
            ...formData,
            mailingAddress:
                formData.serviceAsMailingAddress == true ? formData.serviceAddress : formData.mailingAddress,
            billingAddress: formData.mailingAddress
        });

        setShowSaveModal(true);
    };

    const saveFormData = () => {
        context.updateFormData(formData);
    };

    const onEditAddress = () => {
        setIsLoading(false);
        setIsOpenUnableAddressModal(false);
        setIsOpenAddressValidationModal(false);
    };

    const phoneError = useCallback(
        () => simpleValidator.current.message('phone', formData.phone, 'required|phone|valid_phone'),
        [formData.phone]
    );
    const { variableText } = getClientContent(clientId);
    const phoneNumber = variableText.customerService.phone;
    const dynamicContent = variableText?.salesConfig || {};

    return (
        <div className='container contentWrapper'>
            <Form>
                <Row>
                    <Col
                        md='7'
                        lg='6'
                        mb='3'
                    >
                        <Col
                            md='9'
                            id='serviceAddressFields'
                            className='p-0 mb-3'
                        >
                            <h1 className='h5'>Your Details</h1>
                            <FormGroup className='row twoColWrapper mb-0'>
                                <Col
                                    sm='6'
                                    className='mb-xs-0 px-2'
                                >
                                    <FormGroup
                                        className={
                                            !simpleValidator.current.check(formData.firstName, 'required')
                                            && validationCheck
                                            && 'form-error'
                                        }
                                    >
                                        <StandardInput
                                            includeFormGroupWrapper={false}
                                            label='First Name'
                                            fieldName='firstName'
                                            ariaLabelAndFieldId='firstName'
                                            maxLength={14}
                                            autoComplete='given-name'
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            validator={simpleValidator.current.message(
                                                'firstName',
                                                formData.firstName,
                                                'required'
                                            )}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm='6'>
                                    <FormGroup
                                        className={
                                            !simpleValidator.current.check(formData.lastName, 'required')
                                            && validationCheck
                                            && 'form-error'
                                        }
                                    >
                                        <StandardInput
                                            includeFormGroupWrapper={false}
                                            label='Last Name'
                                            fieldName='lastName'
                                            maxLength={15}
                                            ariaLabelAndFieldId='lastName'
                                            autoComplete='family-name'
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            validator={simpleValidator.current.message(
                                                'lastName',
                                                formData.lastName,
                                                'required'
                                            )}
                                        />
                                    </FormGroup>
                                </Col>
                            </FormGroup>
                            <FormGroup className={typeof phoneError() !== 'undefined' && 'form-error'}>
                                <Label
                                    for='phone'
                                    className='form-label'
                                >
                                    Phone
                                </Label>
                                <span
                                    aria-hidden='true'
                                    className='required-indicator'
                                >
                                    *
                                </span>
                                <Cleave
                                    options={{
                                        blocks: [0, 3, 0, 3, 4],
                                        delimiters: ['(', ')', ' ', '-'],
                                        numericOnly: true
                                    }}
                                    type='tel'
                                    className='form-control'
                                    autoComplete='tel'
                                    id='phone'
                                    name='phone'
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    aria-required='true'
                                    validator={simpleValidator.current.message(
                                        'phone',
                                        formData.phone,
                                        'required|phone|valid_phone'
                                    )}
                                />
                                {typeof phoneError() !== 'undefined' ? (
                                    <Label
                                        for='phone'
                                        className='form-label'
                                    >
                                        {phoneError()}
                                    </Label>
                                ) : null}
                            </FormGroup>
                            <StandardInput
                                hasError={
                                    !simpleValidator.current.check(formData.email, 'required|email')
                                    && validationCheck
                                    && 'form-error'
                                }
                                label='Email'
                                type='email'
                                fieldName='email'
                                ariaLabelAndFieldId='email'
                                value={formData.email}
                                name='email'
                                id='email'
                                onChange={handleInputChange}
                                validator={simpleValidator.current.message('email', formData.email, 'required|email')}
                            />
                        </Col>
                        {dynamicContent?.solicitationCheckbox && (
                            <Col className='p-0 mb-3'>
                                <CheckboxInput
                                    clientId={clientId}
                                    id='termsAndCondition'
                                    checked={formData.termsAndCondition}
                                    handleCheck={handleDoNotSolicit}
                                >
                                    I do not wish to receive special offers, promotions or other email marketing
                                    communications from NJR Retail. All information we gather is governed by our&nbsp;
                                    <a
                                        href='https://www.njrhomeservices.com/privacy-disclosures'
                                        target='_blank'
                                        className={`ml-2 ${styles.privacyLink}`}
                                    >
                                        privacy statement
                                    </a>
                                    .
                                </CheckboxInput>
                            </Col>
                        )}
                        {dynamicContent?.showServiceAddress && (
                            <Col
                                md='9'
                                className='p-0 mb-5'
                            >
                                <h2 className='h5'>Service Address</h2>
                                <StandardInput
                                    hasError={
                                        !simpleValidator.current.check(formData.serviceAddress.address1, 'required')
                                        && validationCheck
                                        && 'form-error'
                                    }
                                    label='Address'
                                    fieldName='address1'
                                    maxLength={30}
                                    autoComplete='address-line1'
                                    ariaLabelAndFieldId='address1'
                                    value={formData.serviceAddress.address1}
                                    onChange={handleServiceAddressChange}
                                    validator={simpleValidator.current.message(
                                        'serviceAddress',
                                        formData.serviceAddress.address1,
                                        'required'
                                    )}
                                />
                                <StandardInput
                                    required={false}
                                    label='Apt., Suite, Building'
                                    fieldName='address2'
                                    maxLength={30}
                                    autoComplete='address-line2'
                                    ariaLabelAndFieldId='address2'
                                    value={formData.serviceAddress.address2}
                                    onChange={handleServiceAddressChange}
                                />

                                <StandardInput
                                    hasError={
                                        !simpleValidator.current.check(formData.serviceAddress.city, 'required')
                                        && validationCheck
                                        && 'form-error'
                                    }
                                    label='City'
                                    fieldName='city'
                                    autoComplete='address-line3'
                                    ariaLabelAndFieldId='city'
                                    maxLength={20}
                                    value={formData.serviceAddress.city}
                                    onChange={handleServiceAddressChange}
                                    validator={simpleValidator.current.message(
                                        'city',
                                        formData.serviceAddress.city,
                                        'required'
                                    )}
                                />
                                <FormGroup className='row twoColWrapper'>
                                    <Col sm='6'>
                                        <Label
                                            for='serviceState'
                                            className='form-label'
                                        >
                                            State
                                        </Label>
                                        <span
                                            aria-hidden='true'
                                            className='required-indicator'
                                        >
                                            *
                                        </span>
                                        <span>
                                            <InfoTooltip
                                                infoType='questionInfo'
                                                message='NJR Retail Service Plans are currently only available to New Jersey residents.'
                                            />
                                        </span>
                                        {states.length > 0 && (
                                            <DropDownAutoComplete
                                                name='state'
                                                id='serviceState'
                                                aria-label='Select State'
                                                className='form-control'
                                                options={states}
                                                isDisabled={true}
                                                defaultValue={
                                                    formData.serviceAddress.stateId === ''
                                                    || formData.serviceAddress.stateId === null
                                                        ? undefined
                                                        : formData.serviceAddress.stateId
                                                }
                                                aria-required='true'
                                                Value={formData.serviceAddress.stateId}
                                            />
                                        )}
                                    </Col>
                                    <Col sm='6'>
                                        <FormGroup
                                            className={
                                                !simpleValidator.current.check(
                                                    formData.serviceAddress.zip,
                                                    'required|numeric|size:5'
                                                )
                                                && validationCheck
                                                && 'form-error'
                                            }
                                        >
                                            <StandardInput
                                                includeFormGroupWrapper={false}
                                                label='ZIP/Postal Code'
                                                fieldName='zip'
                                                ariaLabelAndFieldId='zip'
                                                autoComplete='postal-code'
                                                maxLength={5}
                                                value={formData.serviceAddress.zip}
                                                onChange={handleServiceAddressChange}
                                                validator={simpleValidator.current.message(
                                                    'zip/postalCode',
                                                    formData.serviceAddress.zip,
                                                    'required|numeric|size:5'
                                                )}
                                            />
                                        </FormGroup>
                                    </Col>
                                </FormGroup>
                            </Col>
                        )}
                        <Col
                            md='9'
                            className={!formData.serviceAsMailingAddress ? 'p-0 mb-5' : 'p-0 mb-4'}
                        >
                            <h2 className='h5'>Mailing Address</h2>
                            {dynamicContent?.useServiceAddress && (
                                <CheckboxInput
                                    clientId={clientId}
                                    id='mailingAddress'
                                    checked={formData.serviceAsMailingAddress}
                                    handleCheck={handleServiceAsMailing}
                                >
                                    Same as service address
                                </CheckboxInput>
                            )}
                            {(!formData.serviceAsMailingAddress || !dynamicContent?.useServiceAddress) && (
                                <>
                                    <StandardInput
                                        hasError={
                                            !simpleValidator.current.check(formData.mailingAddress.address1, 'required')
                                            && validationCheck
                                            && 'form-error'
                                        }
                                        label='Address'
                                        fieldName='address1'
                                        autoComplete='address-line1'
                                        ariaLabelAndFieldId='mailingAddress1'
                                        value={formData.mailingAddress.address1}
                                        onChange={handleMailingAddressChange}
                                        validator={simpleValidator.current.message(
                                            'mailingAddress',
                                            formData.mailingAddress.address1,
                                            'required'
                                        )}
                                    />
                                    <StandardInput
                                        required={false}
                                        label='Apt., Suite, Building'
                                        fieldName='address2'
                                        autoComplete='address-line2'
                                        ariaLabelAndFieldId='mailingAddress2'
                                        value={formData.mailingAddress.address2}
                                        onChange={handleMailingAddressChange}
                                    />

                                    <StandardInput
                                        hasError={
                                            !simpleValidator.current.check(formData.mailingAddress.city, 'required')
                                            && validationCheck
                                            && 'form-error'
                                        }
                                        label='City'
                                        fieldName='city'
                                        autoComplete='address-line3'
                                        ariaLabelAndFieldId='mailingCity'
                                        value={formData.mailingAddress.city}
                                        onChange={handleMailingAddressChange}
                                        validator={simpleValidator.current.message(
                                            'city',
                                            formData.mailingAddress.city,
                                            'required'
                                        )}
                                    />

                                    <FormGroup className='row twoColWrapper'>
                                        <Col sm='6'>
                                            <FormGroup
                                                className={
                                                    !simpleValidator.current.check(
                                                        formData.mailingAddress.state,
                                                        'required'
                                                    )
                                                    && validationCheck
                                                    && 'form-error'
                                                }
                                            >
                                                <Label
                                                    for='mailingState'
                                                    className='form-label'
                                                >
                                                    State
                                                </Label>
                                                <span
                                                    aria-hidden='true'
                                                    className='required-indicator'
                                                >
                                                    *
                                                </span>
                                                {states.length > 0 && (
                                                    <div
                                                        className={classNames(
                                                            !simpleValidator.current.check(
                                                                formData.mailingAddress.state,
                                                                'required'
                                                            )
                                                                && validationCheck
                                                                && 'form-control',
                                                            styles.formControlZip
                                                        )}
                                                    >
                                                        <DropDownAutoComplete
                                                            name='state'
                                                            options={states}
                                                            className='form-control'
                                                            aria-required='true'
                                                            id='mailingState'
                                                            onChangedValue={handleStateDropDown}
                                                            isFloatingDropdown={false}
                                                            onBlur={handleStateDropDown}
                                                            defaultValue={
                                                                formData.mailingAddress.stateId === ''
                                                                    ? undefined
                                                                    : formData.mailingAddress.stateId
                                                            }
                                                            Value={formData.mailingAddress.stateId}
                                                        />
                                                    </div>
                                                )}
                                                {simpleValidator.current.message(
                                                    'state',
                                                    formData.mailingAddress.state,
                                                    'required'
                                                )}
                                            </FormGroup>
                                        </Col>

                                        <Col sm='6'>
                                            <FormGroup
                                                className={
                                                    !simpleValidator.current.check(
                                                        formData.mailingAddress.zip,
                                                        'required|numeric|size:5'
                                                    )
                                                    && validationCheck
                                                    && 'form-error'
                                                }
                                            >
                                                <StandardInput
                                                    includeFormGroupWrapper={false}
                                                    label='ZIP/Postal Code'
                                                    fieldName='zip'
                                                    maxLength='5'
                                                    ariaLabelAndFieldId='mailingZip'
                                                    autoComplete='postal-code'
                                                    value={formData.mailingAddress.zip}
                                                    onChange={handleMailingAddressChange}
                                                    validator={simpleValidator.current.message(
                                                        'zip/postalCode',
                                                        formData.mailingAddress.zip,
                                                        'required|numeric|size:5'
                                                    )}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </FormGroup>
                                </>
                            )}
                        </Col>
                        <Col
                            md='9'
                            className='p-0 mb-3'
                        >
                            <div className={styles.customerInfoBtnWrapper}>
                                <button
                                    role='button'
                                    className={classNames(btnsecondary, btn)}
                                    aria-label='Click back to open shop plans screen'
                                    onClick={handleBackbutton}
                                >
                                    Back
                                </button>
                                <SpinnerButton
                                    text='Continue'
                                    onClick={handleContinue}
                                    ariaLabel='Click continue to open to review the plan details screen'
                                    spinning={isLoading}
                                    disabled={isLoading}
                                />
                            </div>
                            {dynamicContent?.showCancelSale && (
                                <div className={styles.btnContainer}>
                                    <a
                                        className='btnLink'
                                        aria-label='Opens a modal to cancel sale'
                                        onClick={() => setShowCancelSaleModal(true)}
                                    >
                                        Cancel Sale
                                    </a>
                                </div>
                            )}
                            {dynamicContent?.showSaveSale && (
                                <div className={styles.saveLaterBtn}>
                                    <a
                                        href='#'
                                        onClick={handleShowActionModal}
                                        className={classNames(btnLink, styles.saveForLater)}
                                        aria-label='Opens a modal to save your information'
                                    >
                                        Save For Later
                                    </a>
                                </div>
                            )}
                        </Col>
                    </Col>
                    <Col
                        className='mb-3'
                        md={{ size: '5', offset: 0 }}
                        lg={{ size: '5', offset: 1 }}
                    >
                        <OrderSummary
                            saveFormData={saveFormData}
                            clientId={clientId}
                        />
                        {dynamicContent?.showCallBanner && <CallBanner phoneNumber={phoneNumber} />}
                    </Col>
                </Row>

                {isOpenAddressValidationModal && (
                    <RecommendedAddressModal
                        enteredAddress={formData}
                        suggestedAddress={recommendedAddress}
                        showServiceAddress={showServiceAddress}
                        showMailingAddress={showMailingAddress}
                        hideModal={onEditAddress}
                        saveVerifiedAddress={saveVerifiedAddress}
                    />
                )}

                {isOpenUnableAddressModal && (
                    <IncorrectAddressModal
                        enteredAddress={formData}
                        hideModal={onEditAddress}
                        saveVerifiedAddress={saveVerifiedAddress}
                        showServiceAddress={showServiceAddress}
                        showMailingAddress={showMailingAddress}
                    />
                )}

                {showSaveModal && (
                    <SaveModal
                        isOpen={true}
                        centered={true}
                        toggle={() => setShowSaveModal(false)}
                        customerData={formData}
                        planData={planData}
                        checked={formData.termsAndCondition}
                        clientId={clientId}
                        isSavedForLater={handleSavedData}
                        comingFrom={'CustomerInfo'}
                        isAgent={isAgent}
                        promoCodeData={context.data.isPromoApplied ? context.data.promocodeObj : null}
                    />
                )}

                {showCancelSaleModal && (
                    <CancelSaleModal
                        toggle={() => setShowCancelSaleModal(false)}
                        isOpen={showCancelSaleModal}
                        cancelAction={handleCancelSale}
                    />
                )}
            </Form>
        </div>
    );
};

export default CustomerInfo;
